import React from "react"
import {
  Input, InputGroup, InputRightAddon, Text,
} from "@chakra-ui/react"
import { Control, UseFormSetValue, useWatch } from "react-hook-form"
import SidePanelHeader from "components/Builder/components/SidePanelHeader"
import EditorSection from "components/Builder/components/EditorSection"
import RadioButtons from "components/ReactHookForm/RadioButtons"
import TextField from "components/ReactHookForm/TextField"
import { Form, FormType } from "sharedTypes"
import { submitButtonPriceSuffix, templateSubmitButtonLabel } from "utilities/forms"
import MessagingSection from "../EditFormModalContent/EditFormModalSettings/Messaging"

export type SubmitButtonLabelMode = "default" | "override"

// Panel-only values that live in currentForm, never on the API: the chosen mode and
// the typed text, kept while the template is selected so switching back restores it.
// submitButtonLabel itself (what the canvas and the save read) is derived from them.
export type FormWithSubmitButtonLabelMode = Form & {
  submitButtonLabelMode?: SubmitButtonLabelMode
  submitButtonCustomLabel?: string
}

const SUBMIT_BUTTON_LABEL_MODE_OPTIONS: { label: string; value: SubmitButtonLabelMode }[] = [
  { label: "Use Language Template", value: "default" },
  { label: "Custom", value: "override" },
]

type Props = {
  form: Form
  control: Control
  setValue: UseFormSetValue<any>
}

// Same default/override choice as the Wallet Pass builder: the field stays visible
// while the template is in use, read-only, so the organizer sees what they would override.
const SubmissionSettings = ({
  form,
  control,
  setValue,
}: Props) => {
  const mode: SubmitButtonLabelMode = useWatch({ control, name: "submitButtonLabelMode" })
  const customLabel: string = useWatch({ control, name: "submitButtonCustomLabel" })
  const templateLabel = templateSubmitButtonLabel(form)
  const priceAddon = form.type === FormType.Ticket ? submitButtonPriceSuffix(form) : ""

  return (
    <>
      <SidePanelHeader>Button</SidePanelHeader>
      <EditorSection>
        <Text fontSize="lg" fontWeight="bold" mb={3}>Button Label</Text>
        <RadioButtons
          name="submitButtonLabelMode"
          control={control}
          options={SUBMIT_BUTTON_LABEL_MODE_OPTIONS}
          horizontal
          optionWidth="auto"
          spacing={8}
          mb={3}
          alignItems="center"
          onChange={(value: SubmitButtonLabelMode) => {
            setValue("submitButtonLabel", value === "override" ? customLabel : "")
          }}
        />
        {mode === "override" ? (
          <TextField
            name="submitButtonCustomLabel"
            control={control}
            onChange={(event) => setValue("submitButtonLabel", event.target.value)}
            ariaLabel="Button label"
            variant="outline"
            maxLength={40}
            rightAddon={priceAddon || undefined}
            fullWidth
            mb={4}
          />
        ) : (
          <InputGroup mb={4}>
            <Input
              value={templateLabel}
              variant="outline"
              isReadOnly
              color="gray.500"
              tabIndex={-1}
              aria-label="Button label from the language template"
              onChange={() => {}}
            />
            {priceAddon && <InputRightAddon>{priceAddon}</InputRightAddon>}
          </InputGroup>
        )}
      </EditorSection>
      <MessagingSection form={form} />
    </>
  )
}

export default SubmissionSettings
